// Orchestrates a model's baked render asset for Layout placement: an in-memory
// core cache, an Object3D clone cache, in-flight dedup, the separate IndexedDB
// bake store, and the off-thread bake worker. The placement sites + the save
// path go through here so model-bake.ts can stay pure THREE builders.
//
// Placement is "shell now, asset when ready": cloneForModel() returns a clone
// synchronously (the cached asset, or a fast shell-only mesh); ensureAsset()
// fetches-or-computes the real asset in the background, after which the caller
// re-clones (via placementSystem.rebuildModel) to upgrade.

import * as THREE from "three";
import {
  type BakedAssetCore,
  type SavedModel,
  type SavedModelV2,
  isV2,
} from "../model/saved-model";
import { getBake, putBake } from "../model/bake-store";
import type { PaletteLookup } from "../view/voxel-mesh";
import { bakedAssetToObject3D, buildBaked } from "./model-bake";
import { requestBake } from "./bake-worker";

interface CacheEntry {
  core: BakedAssetCore;
  updatedAt: number;
}

const assetCache = new Map<string, CacheEntry>();
// Built Object3D per model id (cloned per placement; clones share geometry).
const cloneCache = new Map<string, THREE.Object3D>();
// Dedup concurrent ensureAsset/ensureAO for the same model.
const inFlight = new Map<string, Promise<boolean>>();

function cached(model: SavedModel): CacheEntry | null {
  const e = assetCache.get(model.id);
  return e && e.updatedAt === model.updatedAt ? e : null;
}

// True if a current-revision asset is in memory (placement can use it directly).
export function hasAsset(model: SavedModel): boolean {
  return cached(model) !== null;
}

// True if the cached asset already includes the AO mesh (presentation look).
export function hasAO(model: SavedModel): boolean {
  const e = cached(model);
  return !!e && !!e.core.ao;
}

function storeCore(id: string, updatedAt: number, core: BakedAssetCore): void {
  assetCache.set(id, { core, updatedAt });
  cloneCache.delete(id); // force a rebuild from the new core on next clone
}

// A placement-ready clone: the cached asset if present, else a fast shell-only
// mesh (the AO pass is skipped — it's heavy and only shown in presentation).
export function cloneForModel(model: SavedModel, palette: PaletteLookup): THREE.Object3D {
  let obj = cloneCache.get(model.id);
  if (!obj) {
    const entry = cached(model);
    obj = entry
      ? bakedAssetToObject3D(entry.core, model, palette)
      : buildBaked(model, palette, false);
    cloneCache.set(model.id, obj);
  }
  return obj.clone(true);
}

// Ensure a current-revision SHELL asset is cached. `force` (the save path)
// always recomputes. Returns true if an asset is now available, false if the
// model is legacy/unbakeable or the worker is unavailable (caller stays
// shell-only). Concurrent calls share one in-flight promise.
export function ensureAsset(
  model: SavedModel,
  opts: { force?: boolean } = {},
): Promise<boolean> {
  if (!isV2(model)) return Promise.resolve(false);
  if (!opts.force && hasAsset(model)) return Promise.resolve(true);
  const existing = inFlight.get(model.id);
  if (existing && !opts.force) return existing;
  const p = runEnsure(model, false, opts.force ?? false);
  trackInFlight(model.id, p);
  return p;
}

// Ensure the cached asset INCLUDES AO (for presentation lighting). Recomputes
// with the AO pass if the current asset lacks it.
export function ensureAO(model: SavedModel): Promise<boolean> {
  if (!isV2(model)) return Promise.resolve(false);
  const entry = cached(model);
  if (entry && entry.core.ao) return Promise.resolve(true);
  const existing = inFlight.get(model.id);
  if (existing) return existing.then(() => ensureAO(model)); // recheck after it lands
  const p = runEnsure(model, true, false);
  trackInFlight(model.id, p);
  return p;
}

async function runEnsure(model: SavedModelV2, withAO: boolean, force: boolean): Promise<boolean> {
  if (!force) {
    const stored = await getBake(model.id);
    if (stored && stored.updatedAt === model.updatedAt && (!withAO || stored.ao)) {
      storeCore(model.id, model.updatedAt, stored);
      return true;
    }
  }
  const core = await requestBake(model.voxels, withAO);
  if (!core) return false;
  storeCore(model.id, model.updatedAt, core);
  await putBake({ id: model.id, updatedAt: model.updatedAt, ...core });
  return true;
}

function trackInFlight(id: string, p: Promise<boolean>): void {
  inFlight.set(id, p);
  void p.finally(() => {
    if (inFlight.get(id) === p) inFlight.delete(id);
  });
}

// Drop the built Object3D for a model id (e.g. after a save-over) so the next
// cloneForModel rebuilds. Does not evict the core cache.
export function dropClone(id: string): void {
  cloneCache.delete(id);
}
export function clearClones(): void {
  cloneCache.clear();
}
